import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient from '../utils/apiClient';
import {
    Container,
    Typography,
    Box,
    Card,
    CardContent,
    Button,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Paper
} from '@mui/material';
import DragIcon from '@mui/icons-material/DragIndicator';
import AddIcon from '@mui/icons-material/Add';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

const ExerciseDetail = () => {
    const { sessionId, exerciseId } = useParams();
    const navigate = useNavigate();
    const [exercise, setExercise] = useState({
        id: '',
        name: '',
        description: '',
        nom_moteur: '',
        nom_cognitif: '',
        text_moteur: '',
        text_cognitif: '',
        vocal_moteur: '',
        vocal_moteur_mp3: '',
        vocal_cognitif: '',
        vocal_cognitif_mp3: '',
        thumbnail: '',
        thumbnail_cognitif: '',
        materiel: [],
        type: '',
        timelines: []
    });
    const [timelines, setTimelines] = useState([]);
    const [openTimelineDialog, setOpenTimelineDialog] = useState(false);
    const [openExerciseDialog, setOpenExerciseDialog] = useState(false);
    const [editingTimeline, setEditingTimeline] = useState(null);
    const [editingExercise, setEditingExercise] = useState(null);
    const [timelineTimestamp, setTimelineTimestamp] = useState('');
    const [exerciseName, setExerciseName] = useState('');
    const [exerciseDescription, setExerciseDescription] = useState('');
    const [exerciseThumbnail, setExerciseThumbnail] = useState('');
    const [exerciseThumbnailCognitif, setExerciseThumbnailCognitif] = useState('');
    const [exerciseMateriel, setExerciseMateriel] = useState([]);
    const [exerciseType, setExerciseType] = useState('');
    const [openInstructionDialog, setOpenInstructionDialog] = useState(false);
    const [editingInstruction, setEditingInstruction] = useState(null);
    const [currentTimeline, setCurrentTimeline] = useState(null);
    const [instructionForm, setInstructionForm] = useState({
        type: 'moteur',
        text_affichage: '',
        texte_vocal: '',
        video_url: '',
        image: ''
    });

    useEffect(() => {
        const fetchExercise = async () => {
            try {
                const response = await apiClient.get(`/sessions/${sessionId}`);
                const found = response.data?.exercises?.find(e => String(e.id) === String(exerciseId));
                if (found) {
                    // Normaliser le matériel pour s'assurer que c'est toujours un tableau
                    const normalizedExercise = {
                        ...found,
                        materiel: Array.isArray(found.materiel) 
                            ? found.materiel 
                            : (found.materiel ? [found.materiel] : []),
                        type: found.type || ''
                    };
                    setExercise(normalizedExercise);
                }
            } catch (error) {
                console.error('Erreur lors de la récupération de l\'exercice: ', error);
            }
        };
        fetchExercise();
    }, [sessionId, exerciseId]);

    useEffect(() => {
        if (exercise) setTimelines(exercise.timelines || []);
    }, [exercise]);

    // Fonction pour vérifier si l'URL est une vidéo valide
    const isValidVideoUrl = (url) => {
        if (!url || typeof url !== 'string') return false;
        
        const videoExtensions = ['.mp4', '.webm', '.ogg', '.avi', '.mov', '.wmv', '.flv', '.mkv', '.m4v'];
        const lowerUrl = url.toLowerCase();
        
        // Vérifier l'extension
        const hasValidExtension = videoExtensions.some(ext => lowerUrl.includes(ext));
        
        // Vérifier si c'est une URL valide
        const isValidUrl = url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:video/');
        
        return hasValidExtension && isValidUrl;
    };

    const handleOpenTimelineDialog = (timeline) => {
        setEditingTimeline(timeline || null);
        setTimelineTimestamp(timeline ? timeline.timestamp : '');
        setOpenTimelineDialog(true);
    };
    const handleCloseTimelineDialog = () => {
        setOpenTimelineDialog(false);
        setEditingTimeline(null);
        setTimelineTimestamp('');
    };
    const handleSaveTimeline = async () => {
        if (!editingTimeline) return;
        const timestamp = parseInt(timelineTimestamp, 10);
        if (isNaN(timestamp)) {
            alert('Le timestamp doit être un nombre entier');
            return;
        }
        const updatedTimeline = { ...editingTimeline, timestamp };
        try {
            await apiClient.put(`/sessions/${sessionId}/exercises/${exerciseId}/timelines/${editingTimeline.id}`, updatedTimeline);
            const newTimelines = timelines.map(t => t.id === editingTimeline.id ? updatedTimeline : t);
            setTimelines(newTimelines);
            setExercise({ ...exercise, timelines: newTimelines });
            handleCloseTimelineDialog();
        } catch (error) {
            console.error('Erreur lors de la mise à jour de la timeline:', error);
        }
    };
    const handleAddTimeline = async () => {
        const timestamp = parseInt(timelineTimestamp, 10);
        if (isNaN(timestamp)) {
            alert('Le timestamp doit être un nombre entier');
            return;
        }
        const newTimeline = {
            id: `timeline_${Date.now()}`,
            timestamp,
            instructions: []
        };
        try {
            const response = await apiClient.post(`/sessions/${sessionId}/exercises/${exerciseId}/timelines`, newTimeline);
            const updatedTimeline = response.data;
            const newTimelines = [...timelines, updatedTimeline];
            setTimelines(newTimelines);
            setExercise({ ...exercise, timelines: newTimelines });
            handleCloseTimelineDialog();
        } catch (error) {
            console.error('Erreur lors de l\'ajout de la timeline:', error);
        }
    };
    const handleDeleteTimeline = async (timeline) => {
        try {
            await apiClient.delete(`/sessions/${sessionId}/exercises/${exerciseId}/timelines/${timeline.id}`);
            const newTimelines = timelines.filter(t => t.id !== timeline.id);
            setTimelines(newTimelines);
            setExercise({ ...exercise, timelines: newTimelines });
        } catch (error) {
            console.error('Erreur lors de la suppression de la timeline:', error);
        }
    };
    const handleDragEnd = async (result) => {
        if (!result.destination) return;
        const items = Array.from(timelines);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);
        try {
            await apiClient.put(`/sessions/${sessionId}/exercises/${exerciseId}/timelines/order`, { timelines: items });
            setTimelines(items);
            setExercise({ ...exercise, timelines: items });
        } catch (error) {
            console.error('Erreur lors de la mise à jour de l\'ordre des timelines:', error);
        }
    };

    const handleOpenInstructionDialog = (timeline, instruction) => {
        setCurrentTimeline(timeline);
        setEditingInstruction(instruction || null);
        setInstructionForm(instruction ? { ...instruction } : {
            type: 'moteur',
            text_affichage: '',
            texte_vocal: '',
            video_url: '',
            text_mise_en_avant: '',
            image: ''
        });
        setOpenInstructionDialog(true);
    };
    const handleCloseInstructionDialog = () => {
        setOpenInstructionDialog(false);
        setEditingInstruction(null);
        setCurrentTimeline(null);
    };
    const handleInstructionFormChange = (e) => {
        const { name, value } = e.target;
        setInstructionForm(prev => ({ ...prev, [name]: value }));
    };
    const handleSaveInstruction = async () => {
        if (!currentTimeline) return;
        let newInstructions;
        if (editingInstruction) {
            // Modification
            newInstructions = currentTimeline.instructions.map(i => i === editingInstruction ? { ...instructionForm, id: editingInstruction.id } : i);
        } else {
            // Ajout
            const newInstruction = { ...instructionForm, id: `instruction_${Date.now()}` };
            newInstructions = [...(currentTimeline.instructions || []), newInstruction];
        }
        await saveInstructions(currentTimeline, newInstructions);
        handleCloseInstructionDialog();
    };
    const handleDeleteInstruction = async (timeline, instruction) => {
        const newInstructions = timeline.instructions.filter(i => i.id !== instruction.id);
        await saveInstructions(timeline, newInstructions);
    };
    const handleInstructionDragEnd = async (result, timeline) => {
        if (!result.destination) return;
        const items = Array.from(timeline.instructions);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);
        await saveInstructions(timeline, items);
    };
    const saveInstructions = async (timeline, newInstructions) => {
        // Met à jour la timeline côté API
        try {
            await apiClient.put(`/sessions/${sessionId}/exercises/${exerciseId}/timelines/${timeline.id}`, {
                ...timeline,
                instructions: newInstructions
            });
            // Met à jour localement
            const newTimelines = timelines.map(t => t.id === timeline.id ? { ...t, instructions: newInstructions } : t);
            setTimelines(newTimelines);
            setExercise({ ...exercise, timelines: newTimelines });
        } catch (error) {
            console.error('Erreur lors de la mise à jour des instructions:', error);
        }
    };

    const handleCloseExerciseDialog = () => {
        setOpenExerciseDialog(false);
        setEditingExercise(null);
        setExerciseName('');
        setExerciseDescription('');
        setExerciseThumbnail('');
        setExerciseThumbnailCognitif('');
        setExerciseMateriel([]);
        setExerciseType('');
    };

    const handleSaveExercise = async () => {
        if (!exerciseName.trim()) {
            alert('Le nom de l\'exercice est requis');
            return;
        }

        const exerciseData = {
            name: exerciseName,
            description: exerciseDescription,
            name_moteur: exercise.name_moteur,
            name_cognitif: exercise.name_cognitif,
            text_moteur: exercise.text_moteur,
            text_cognitif: exercise.text_cognitif,
            vocal_moteur: exercise.vocal_moteur,
            vocal_moteur_mp3: exercise.vocal_moteur_mp3,
            vocal_cognitif: exercise.vocal_cognitif,
            vocal_cognitif_mp3: exercise.vocal_cognitif_mp3,
            thumbnail: exerciseThumbnail,
            thumbnail_cognitif: exerciseThumbnailCognitif,
            materiel: exerciseMateriel,
            type: exerciseType
        };

        try {
            if (editingExercise) {
                // Modification
                const res = await apiClient.put(`/sessions/${sessionId}/exercises/${exerciseId}`, exerciseData);
                setExercise(res.data);
            } else {
                // Création
                const res = await apiClient.post(`/sessions/${sessionId}/exercises`, exerciseData);
                setExercise(res.data);
                navigate(`/sessions/${sessionId}/exercises/${res.data.id}`);
            }
            handleCloseExerciseDialog();
        } catch (error) {
            console.error('Erreur lors de la sauvegarde de l\'exercice:', error);
        }
    };

    if (!exercise) {
        return <Typography>Chargement...</Typography>;
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                    <Box>
                        <Typography variant="h4" gutterBottom>
                            {exercise.name}
                        </Typography>
                        <Typography variant="subtitle1" gutterBottom>
                            Nom motrice
                        </Typography>
                        <Typography variant="body1" color="text.secondary" paragraph>
                            {exercise.nom_moteur}
                        </Typography>
                        <Typography variant="subtitle1" gutterBottom>
                            Description motrice
                        </Typography>
                        <Typography variant="body1" color="text.secondary" paragraph>
                            {exercise.text_moteur}
                        </Typography>
                        <Typography variant="subtitle1" gutterBottom>
                            Texte vocal motrice
                        </Typography>
                        <Typography variant="body1" color="text.secondary" paragraph>
                            {exercise.vocal_moteur}
                        </Typography>
                        <Typography variant="subtitle1" gutterBottom>
                            Fichier MP3 vocal motrice
                        </Typography>
                        <Typography variant="body1" color="text.secondary" paragraph>
                            {exercise.vocal_moteur_mp3 ? (
                                <a href={exercise.vocal_moteur_mp3} target="_blank" rel="noopener noreferrer">
                                    {exercise.vocal_moteur_mp3}
                                </a>
                            ) : (
                                'Aucun fichier MP3'
                            )}
                        </Typography>
                        <Typography variant="subtitle1" gutterBottom>
                            Nom cognitive
                        </Typography>
                        <Typography variant="body1" color="text.secondary" paragraph>
                            {exercise.nom_cognitif}
                        </Typography>
                        <Typography variant="subtitle1" gutterBottom>
                            Description cognitive
                        </Typography>
                        <Typography variant="body1" color="text.secondary" paragraph>
                            {exercise.text_cognitif}
                        </Typography>
                        <Typography variant="subtitle1" gutterBottom>
                            Texte vocal cognitive
                        </Typography>
                        <Typography variant="body1" color="text.secondary" paragraph>
                            {exercise.vocal_cognitif}
                        </Typography>
                        <Typography variant="subtitle1" gutterBottom>
                            Fichier MP3 vocal cognitive
                        </Typography>
                        <Typography variant="body1" color="text.secondary" paragraph>
                            {exercise.vocal_cognitif_mp3 ? (
                                <a href={exercise.vocal_cognitif_mp3} target="_blank" rel="noopener noreferrer">
                                    {exercise.vocal_cognitif_mp3}
                                </a>
                            ) : (
                                'Aucun fichier MP3'
                            )}
                        </Typography>
                        {exercise.thumbnail && (
                            <Box sx={{ mt: 2, mb: 2 }}>
                                <Typography variant="subtitle1" gutterBottom>
                                    Thumbnail
                                </Typography>
                                <img
                                    src={exercise.thumbnail}
                                    alt={`Thumbnail de ${exercise.name}`}
                                    style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover' }}
                                />
                            </Box>
                        )}
                        {exercise.thumbnail_cognitif && (
                            <Box sx={{ mt: 2, mb: 2 }}>
                                <Typography variant="subtitle1" gutterBottom>
                                    Thumbnail Cognitif (Nom du fichier)
                                </Typography>
                                <Typography variant="body1" color="text.secondary" paragraph>
                                    {exercise.thumbnail_cognitif}
                                </Typography>
                            </Box>
                        )}
                        {exercise.type && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle1" gutterBottom>
                                    Type
                                </Typography>
                                <Typography variant="body1" color="text.secondary">
                                    {exercise.type === 'echauffement' ? 'Echauffement' : 
                                     exercise.type === 'exercice' ? 'Exercice' : 
                                     exercise.type === 'etirement' ? 'Etirement' : exercise.type}
                                </Typography>
                            </Box>
                        )}
                        {exercise.materiel && exercise.materiel.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle1" gutterBottom>
                                    Matériel nécessaire
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {exercise.materiel.map((item, index) => (
                                        <Box
                                            key={index}
                                            sx={{
                                                px: 2,
                                                py: 0.5,
                                                bgcolor: 'primary.light',
                                                color: 'primary.contrastText',
                                                borderRadius: 1,
                                                fontSize: '0.875rem'
                                            }}
                                        >
                                            {item}
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </Box>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            setEditingExercise(exercise);
                            setExerciseName(exercise.name);
                            setExerciseDescription(exercise.description);
                            setExerciseThumbnail(exercise.thumbnail);
                            setExerciseThumbnailCognitif(exercise.thumbnail_cognitif || '');
                            // Normaliser le matériel pour s'assurer que c'est un tableau
                            const materiel = Array.isArray(exercise.materiel) 
                                ? exercise.materiel 
                                : (exercise.materiel ? [exercise.materiel] : []);
                            setExerciseMateriel(materiel);
                            setExerciseType(exercise.type || '');
                            setOpenExerciseDialog(true);
                        }}
                    >
                        Modifier l'exercice
                    </Button>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button
                        variant="outlined"
                        color="secondary"
                        onClick={() => navigate(`/sessions/${sessionId}`)}
                    >
                        Retour à la session
                    </Button>
                </Box>

                {/* Affichage des timelines et instructions si besoin */}
                <Box sx={{ mt: 4 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h5">Timelines</Typography>
                        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenTimelineDialog(null)}>
                            Ajouter une timeline
                        </Button>
                    </Box>
                    <DragDropContext onDragEnd={handleDragEnd}>
                        <Droppable droppableId="timelines-droppable">
                            {(provided) => (
                                <div ref={provided.innerRef} {...provided.droppableProps}>
                                    <Grid container spacing={2}>
                                        {timelines.map((timeline, idx) => (
                                            <Draggable key={String(idx)} draggableId={String(idx)} index={idx}>
                                                {(provided) => (
                                                    <Grid item xs={12} ref={provided.innerRef} {...provided.draggableProps} style={{ ...provided.draggableProps.style, width: '100%' }}>
                                                        <Card>
                                                            <CardContent>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                        <Box {...provided.dragHandleProps} sx={{ cursor: 'grab', mr: 1 }}>
                                                                            <DragIcon />
                                                                        </Box>
                                                                        <Typography variant="subtitle1">Timestamp : {timeline.timestamp} sec</Typography>
                                                                    </Box>
                                                                    <Box>
                                                                        <Button size="small" variant="outlined" sx={{ mr: 1 }} onClick={() => handleOpenTimelineDialog(timeline)}>Modifier</Button>
                                                                        <Button size="small" variant="outlined" color="error" onClick={() => handleDeleteTimeline(timeline)}>Supprimer</Button>
                                                                    </Box>
                                                                </Box>
                                                                {/* Affichage des instructions */}
                                                                {timeline.instructions && (
                                                                    <Box sx={{ mt: 2, ml: 4 }}>
                                                                        <Typography variant="subtitle2">Instructions</Typography>
                                                                        <DragDropContext onDragEnd={result => handleInstructionDragEnd(result, timeline)}>
                                                                            <Droppable droppableId={`instructions-${timeline.id}`}>
                                                                                {(provided) => (
                                                                                    <div ref={provided.innerRef} {...provided.droppableProps}>
                                                                                        {timeline.instructions.map((inst, idx) => (
                                                                                            <Draggable key={inst.id} draggableId={inst.id} index={idx}>
                                                                                                {(provided) => (
                                                                                                    <Box ref={provided.innerRef} {...provided.draggableProps} sx={{ mb: 2 }}>
                                                                                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                                                                            <Box {...provided.dragHandleProps} sx={{ cursor: 'grab', mr: 1 }}><DragIcon /></Box>
                                                                                                            <Box sx={{ flexGrow: 1 }}>
                                                                                                                <Typography><strong>{inst.type}</strong> : {inst.text_affichage}</Typography>
                                                                                                                <Typography variant="caption">{inst.texte_vocal}</Typography>
                                                                                                            </Box>
                                                                                                            <Button size="small" variant="outlined" sx={{ mr: 1 }} onClick={() => handleOpenInstructionDialog(timeline, inst)}>Modifier</Button>
                                                                                                            <Button size="small" variant="outlined" color="error" onClick={() => handleDeleteInstruction(timeline, inst)}>Supprimer</Button>
                                                                                                        </Box>
                                                                                                    </Box>
                                                                                                )}
                                                                                            </Draggable>
                                                                                        ))}
                                                                                        {provided.placeholder}
                                                                                    </div>
                                                                                )}
                                                                            </Droppable>
                                                                        </DragDropContext>
                                                                        <Button size="small" variant="contained" sx={{ mt: 1 }} onClick={() => handleOpenInstructionDialog(timeline, null)}>Ajouter une instruction</Button>
                                                                    </Box>
                                                                )}
                                                            </CardContent>
                                                        </Card>
                                                    </Grid>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </Grid>
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                    <Dialog open={openTimelineDialog} onClose={handleCloseTimelineDialog}>
                        <DialogTitle>{editingTimeline ? 'Modifier la timeline' : 'Ajouter une timeline'}</DialogTitle>
                        <DialogContent>
                            <TextField
                                autoFocus
                                margin="dense"
                                label="Timestamp (secondes)"
                                type="number"
                                fullWidth
                                value={timelineTimestamp}
                                onChange={e => setTimelineTimestamp(e.target.value)}
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseTimelineDialog}>Annuler</Button>
                            <Button onClick={editingTimeline ? handleSaveTimeline : handleAddTimeline} variant="contained">{editingTimeline ? 'Mettre à jour' : 'Ajouter'}</Button>
                        </DialogActions>
                    </Dialog>
                </Box>
                <Dialog open={openInstructionDialog} onClose={handleCloseInstructionDialog}>
                    <DialogTitle>{editingInstruction ? 'Modifier' : 'Ajouter'} une instruction</DialogTitle>
                    <DialogContent>
                        <TextField
                            select
                            label="Type"
                            name="type"
                            value={instructionForm.type}
                            onChange={handleInstructionFormChange}
                            fullWidth
                            sx={{ mt: 2, mb: 2 }}
                        >
                            <MenuItem value="moteur">Motrice</MenuItem>
                            <MenuItem value="cognitif">Cognitive</MenuItem>
                        </TextField>
                        {instructionForm.type === 'moteur' && (
                            <TextField
                                label="Texte d'affichage"
                                name="text_affichage"
                                value={instructionForm.text_affichage}
                                onChange={handleInstructionFormChange}
                                fullWidth
                                sx={{ mb: 2 }}
                            />
                        )}
                        <TextField
                            label="Texte vocal"
                            name="texte_vocal"
                            value={instructionForm.texte_vocal}
                            onChange={handleInstructionFormChange}
                            fullWidth
                            sx={{ mb: 2 }}
                        />
                        {instructionForm.type === 'moteur' && (
                            <TextField
                                label="URL de la vidéo"
                                name="video_url"
                                value={instructionForm.video_url}
                                onChange={handleInstructionFormChange}
                                fullWidth
                                sx={{ mb: 2 }}
                            />
                        )}
                            {/* Test simple - toujours afficher si on a une URL */}
                            {instructionForm.video_url && instructionForm.video_url.trim() !== '' && isValidVideoUrl(instructionForm.video_url) &&(
                                <Box sx={{ mb: 2, p: 0, borderRadius: 1, width: '100%', textAlign: 'center' }}>
                                    <Typography variant="h6" sx={{ mb: 1 }}>
                                        Preview de la vidéo
                                    </Typography>
                                    <video 
                                        controls 
                                        width="100%" 
                                        style={{ maxWidth: '250px', borderRadius: '8px', border: '1px solid #ccc' }}
                                        preload="metadata"
                                    >
                                        <source src={instructionForm.video_url} type="video/mp4" />
                                        <source src={instructionForm.video_url} type="video/webm" />
                                        <source src={instructionForm.video_url} type="video/ogg" />
                                        <p>Votre navigateur ne supporte pas la lecture de vidéos.</p>
                                        <p>URL: {instructionForm.video_url}</p>
                                    </video>
                                </Box>
                            )}
                        {instructionForm.type === 'cognitif' && (
                            <TextField
                                fullWidth
                                label="Texte de mise en avant"
                                name="text_mise_en_avant"
                                value={instructionForm.text_mise_en_avant}
                                onChange={handleInstructionFormChange}
                                sx={{ mb: 2 }}
                            />
                        )}
                        {instructionForm.type === 'cognitif' && (
                            <TextField
                                label="URL de l'image"
                                name="image"
                                value={instructionForm.image}
                                onChange={handleInstructionFormChange}
                                fullWidth
                                sx={{ mb: 2 }}
                            />
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseInstructionDialog}>Annuler</Button>
                        <Button onClick={handleSaveInstruction} variant="contained">{editingInstruction ? 'Mettre à jour' : 'Ajouter'}</Button>
                    </DialogActions>
                </Dialog>
                <Dialog open={openExerciseDialog} onClose={handleCloseExerciseDialog}>
                    <DialogTitle>
                        {editingExercise ? 'Modifier l\'exercice' : 'Nouvel exercice'}
                    </DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Nom"
                            fullWidth
                            value={exerciseName}
                            onChange={(e) => setExerciseName(e.target.value)}
                        />
                        <TextField
                            margin="dense"
                            label="Description motrice"
                            fullWidth
                            multiline
                            rows={2}
                            value={exercise.text_moteur}
                            onChange={(e) => setExercise({ ...exercise, text_moteur: e.target.value })}
                        />
                        <TextField
                            margin="dense"
                            label="Texte vocal motrice"
                            fullWidth
                            multiline
                            rows={2}
                            value={exercise.vocal_moteur}
                            onChange={(e) => setExercise({ ...exercise, vocal_moteur: e.target.value })}
                            helperText="Texte qui sera lu pour la partie motrice (TTS)"
                        />
                        <TextField
                            margin="dense"
                            label="Fichier MP3 vocal motrice (URL)"
                            fullWidth
                            value={exercise.vocal_moteur_mp3 || ''}
                            onChange={(e) => setExercise({ ...exercise, vocal_moteur_mp3: e.target.value })}
                            helperText="URL du fichier MP3 à lire pour la partie motrice (prioritaire sur le TTS)"
                        />
                        <TextField
                            margin="dense"
                            label="Description cognitive"
                            fullWidth
                            multiline
                            rows={2}
                            value={exercise.text_cognitif}
                            onChange={(e) => setExercise({ ...exercise, text_cognitif: e.target.value })}
                        />
                        <TextField
                            margin="dense"
                            label="Texte vocal cognitive"
                            fullWidth
                            multiline
                            rows={2}
                            value={exercise.vocal_cognitif}
                            onChange={(e) => setExercise({ ...exercise, vocal_cognitif: e.target.value })}
                            helperText="Texte qui sera lu pour la partie cognitive (TTS)"
                        />
                        <TextField
                            margin="dense"
                            label="Fichier MP3 vocal cognitive (URL)"
                            fullWidth
                            value={exercise.vocal_cognitif_mp3 || ''}
                            onChange={(e) => setExercise({ ...exercise, vocal_cognitif_mp3: e.target.value })}
                            helperText="URL du fichier MP3 à lire pour la partie cognitive (prioritaire sur le TTS)"
                        />
                        <TextField
                            margin="dense"
                            label="Thumbnail (URL)"
                            fullWidth
                            value={exerciseThumbnail}
                            onChange={(e) => setExerciseThumbnail(e.target.value)}
                            helperText="URL de l'image miniature de l'exercice"
                        />
                        <TextField
                            margin="dense"
                            label="Thumbnail Cognitif (URL)"
                            fullWidth
                            value={exerciseThumbnailCognitif}
                            onChange={(e) => setExerciseThumbnailCognitif(e.target.value)}
                            helperText="URL de l'image miniature cognitive de l'exercice"
                        />
                        <TextField
                            select
                            margin="dense"
                            label="Type"
                            fullWidth
                            value={exerciseType || ''}
                            onChange={(e) => setExerciseType(e.target.value)}
                            sx={{ mt: 2 }}
                            helperText="Sélectionnez le type d'exercice"
                        >
                            <MenuItem value="echauffement">Echauffement</MenuItem>
                            <MenuItem value="exercice">Exercice</MenuItem>
                            <MenuItem value="etirement">Etirement</MenuItem>
                        </TextField>
                        <TextField
                            select
                            margin="dense"
                            label="Matériel nécessaire"
                            fullWidth
                            value={exerciseMateriel || []}
                            onChange={(e) => {
                                const value = e.target.value;
                                setExerciseMateriel(typeof value === 'string' ? value.split(',') : value);
                            }}
                            SelectProps={{
                                multiple: true,
                                renderValue: (selected) => {
                                    if (!selected || selected.length === 0) {
                                        return '';
                                    }
                                    return Array.isArray(selected) ? selected.join(', ') : selected;
                                }
                            }}
                            sx={{ mt: 2 }}
                            helperText="Sélectionnez un ou plusieurs matériels"
                        >
                            <MenuItem value="Chaise">Une chaise</MenuItem>
                            <MenuItem value="Tapis">Un tapis</MenuItem>
                            <MenuItem value="Poids">Des poids</MenuItem>
                        </TextField>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseExerciseDialog}>Annuler</Button>
                        <Button onClick={handleSaveExercise} color="primary">
                            Enregistrer
                        </Button>
                    </DialogActions>
                </Dialog>
            </Paper>
        </Container>
    );
}

export default ExerciseDetail; 